import { TrendingUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFundValuationsSummary } from '@/hooks/react-query/usePortfolio';
import { formatCurrency, formatDate } from '@/utils/formatters';

interface FundValuationsContentProps {
  fundId: string;
  fundCurrency: string;
}

interface ValuationsTablePlaceholderProps {
  isLoading: boolean;
  error: Error | null;
}

function ValuationsTablePlaceholder({
  isLoading,
  error,
}: ValuationsTablePlaceholderProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-full animate-pulse" />
        <div className="h-8 bg-muted rounded w-full animate-pulse" />
        <div className="h-8 bg-muted rounded w-full animate-pulse" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-12 text-red-500">
        <p>Error loading valuations</p>
        <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
      </div>
    );
  }
  return (
    <div className="text-center py-12 text-muted-foreground">
      <TrendingUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
      <p>No portfolio companies found</p>
      <p className="text-sm">
        Add portfolio companies to see their valuations here
      </p>
    </div>
  );
}

export function FundValuationsContent({
  fundId,
  fundCurrency,
}: FundValuationsContentProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useFundValuationsSummary(fundId);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Portfolio Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 bg-muted rounded w-32 animate-pulse" />
            ) : (
              <div className="text-2xl font-bold">
                {formatCurrency(data?.total_portfolio_value ?? 0, {
                  currency: fundCurrency,
                })}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Companies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 bg-muted rounded w-16 animate-pulse" />
            ) : (
              <div className="text-2xl font-bold">
                {data?.company_count ?? 0}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Valuations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Company Valuations</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading || error || !data?.companies.length ? (
            <ValuationsTablePlaceholder isLoading={isLoading} error={error} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Current Value</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.companies.map((company) => (
                    <TableRow
                      key={company.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/app/portfolio/${company.id}`)}
                    >
                      <TableCell className="font-medium">
                        {company.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {company.investment_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {company.current_valuation != null
                          ? formatCurrency(company.current_valuation, {
                              currency: fundCurrency,
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {company.latest_valuation_date
                          ? formatDate(company.latest_valuation_date)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
